import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
    CUSTOMER_ACCOUNTS_FEATURE_KEY,
    CustomerAccountsState,
    customerAccountsAdapter
} from './customer-accounts.reducer';
import { CustomerAccountStatusValue, CustomerAccountTypeValue } from './customer-account.model';
import { selectOrganizationEntities, organizationsLoading } from '../organizations/organizations.selectors';
import {
    selectOrganizationPartEntities,
    organizationPartsLoading
} from '../organization-parts/organization-part.selectors';

export const customerAccountsState = createFeatureSelector<CustomerAccountsState>(CUSTOMER_ACCOUNTS_FEATURE_KEY);

export const {
    selectAll: selectAllCustomerAccounts,
    selectEntities: selectCustomerAccountEntities,
    selectIds: selectCustomerAccountIds,
    selectTotal: selectTotalCustomerAccounts
} = customerAccountsAdapter.getSelectors(customerAccountsState);

export const getAllCustomerAccounts = selectAllCustomerAccounts;

export const getSelectedCustomerAccount = createSelector(customerAccountsState, (state: CustomerAccountsState) => {
    if (!state.selectedAccountId) return null;
    return state.entities[state.selectedAccountId] || null;
});

export const customerAccountsLoading = createSelector(
    customerAccountsState,
    (state: CustomerAccountsState) => state.loading
);

// Combined loading state that waits for all related data to be loaded
export const allAccountDataLoading = createSelector(
    customerAccountsLoading,
    organizationsLoading,
    organizationPartsLoading,
    (customerAccountsLoading, organizationsLoading, organizationPartsLoading) => {
        return customerAccountsLoading || organizationsLoading || organizationPartsLoading;
    }
);

export const getAccountType = (account: { type?: { code?: string } }): string => {
    return account?.type?.code?.toLowerCase() || 'unknown';
};

export const getAccountSelectorUIState = createSelector(
    getAllCustomerAccounts,
    getSelectedCustomerAccount,
    (accounts, selectedAccount) => {
        return {
            accounts,
            selectedAccount,
            hasMultipleAccounts: accounts.length > 1,
            showSegment: accounts.length === 2,
            showDialog: accounts.length >= 3,
            selectedAccountId: selectedAccount?.id?.toString() || ''
        };
    }
);

export const getAccountsWithDisplayData = createSelector(
    getAllCustomerAccounts,
    getSelectedCustomerAccount,
    selectOrganizationPartEntities,
    selectOrganizationEntities,
    (accounts, selectedAccount, organizationPartEntities, organizationEntities) => {
        return accounts.map((account) => {
            const hasOrganizationPart = account.organizationPartId !== null;
            const organizationPart = hasOrganizationPart ? organizationPartEntities[account.organizationPartId] : null;
            const organization = organizationPart?.parentOrganizationId
                ? organizationEntities[organizationPart.parentOrganizationId]
                : null;
            const isNonPersonal = account.type.code !== CustomerAccountTypeValue.PERSONAL;

            return {
                account,
                id: account.id.toString(),
                name: account.type.name.translations,
                typeCode: account.type.code,
                isActive: account.status.value === CustomerAccountStatusValue.ACTIVE,
                isSelected: selectedAccount?.id === account.id,
                organizationPartId: account.organizationPartId,
                isCorporate: account.type.code === CustomerAccountTypeValue.CORPORATE,
                hasOrganizationPart,
                isNonPersonal,
                organizationPart,
                organization
            };
        });
    }
);

export const getSelectedAccountWithDisplayData = createSelector(
    getAccountsWithDisplayData,
    (accountsWithDisplayData) => {
        const selected = accountsWithDisplayData.find((accountData) => accountData.isSelected);
        return selected || null;
    }
);
